// ─── Types ────────────────────────────────────────────────────────────────────

export type BudgetPeriod = {
  start: Date
  end:   Date
  label: string
}

export type BudgetSummary = {
  budget:     number
  spent:      number
  remaining:  number
  percentUsed: number
  daysLeft:   number
  daysTotal:  number
  dailyBudget: number
  projectedSpend: number
  isOverBudget: boolean
  isNearLimit:  boolean
  categoryBreakdown: {
    category:   string
    spent:      number
    percentage: number
  }[]
}

// ─── getCurrentPeriod ────────────────────────────────────────────────────────

export function getCurrentPeriod(): BudgetPeriod {
  const now   = new Date()
  const start = new Date(now.getFullYear(), now.getMonth(), 1)
  const end   = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  const label = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  return { start, end, label }
}

// ─── calculateBudgetSummary ──────────────────────────────────────────────────

export function calculateBudgetSummary(
  budget:        number,
  confirmations: any[],
  period:        BudgetPeriod,
): BudgetSummary {
  const { start: periodStart, end: periodEnd } = period
  const now = new Date()

  const periodConfirmations = confirmations.filter(c => {
    if (!c.price_paid && !c.total_paid) return false
    const date = new Date(c.confirmed_at)
    return date >= periodStart && date <= periodEnd
  })

  // Use total_paid if available (correctly counts multi-unit purchases, e.g. 3 for $10)
  const spent     = periodConfirmations.reduce((sum, c) => sum + (c.total_paid || c.price_paid || 0), 0)
  const remaining = Math.max(0, budget - spent)
  const percentUsed = budget > 0 ? Math.min(100, (spent / budget) * 100) : 0

  const daysTotal = Math.round(
    (periodEnd.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24),
  )
  const daysElapsed = Math.round(
    (now.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24),
  )
  const daysLeft = Math.max(0, daysTotal - daysElapsed)

  const dailySpendRate  = daysElapsed > 0 ? spent / daysElapsed : 0
  const projectedSpend  = dailySpendRate * daysTotal
  const isOverBudget    = spent > budget
  const isNearLimit     = percentUsed >= 80 && !isOverBudget

  // Category breakdown
  const byCategory: Record<string, number> = {}
  for (const c of periodConfirmations) {
    const cat = c.pantry_items?.display_category || 'other'
    byCategory[cat] = (byCategory[cat] || 0) + (c.price_paid || 0)
  }

  const categoryBreakdown = Object.entries(byCategory)
    .map(([category, catSpent]) => ({
      category,
      spent:      catSpent,
      percentage: spent > 0 ? (catSpent / spent) * 100 : 0,
    }))
    .sort((a, b) => b.spent - a.spent)

  return {
    budget,
    spent,
    remaining,
    percentUsed,
    daysLeft,
    daysTotal,
    dailyBudget:    budget / daysTotal,
    projectedSpend,
    isOverBudget,
    isNearLimit,
    categoryBreakdown,
  }
}

// ─── getBudgetStatus ──────────────────────────────────────────────────────────

export function getBudgetStatus(summary: BudgetSummary): {
  color: string
  label: string
  emoji: string
} {
  if (summary.isOverBudget) {
    return { color: '#C25B4E', label: 'Over budget',        emoji: '⚠️' }
  }
  if (summary.isNearLimit) {
    return { color: '#D4863A', label: 'Approaching limit',  emoji: '🔶' }
  }
  if (summary.percentUsed < 50) {
    return { color: '#6B9E78', label: 'On track',           emoji: '✅' }
  }
  return { color: '#6B9E78', label: 'On track', emoji: '👍' }
}
